<script lang="ts" setup>
import type { PropType } from 'vue'

import InputVat from '@/components/inputs/InputVat.vue'
import { isEmptyString, isPostalCode } from '@/utils/validation'

const props = defineProps({
  modelValue: {
    type: Object as PropType<Address>,
    required: true,
  },
})

const emit = defineEmits(['update:modelValue', 'change'])

const languageStore = useLanguageStore()

const isVatDisabled = ref(false)
const postalCode = useTemplateRef<{ validate: () => void }>('postalCode')

const addressData = computed({
  get(): Address {
    return props.modelValue
  },
  set(updatedAddress: Address) {
    emit('update:modelValue', updatedAddress)
  },
})

watch(
  () => addressData.value.country,
  () => postalCode.value?.validate()
)
</script>

<template>
  <v-form @submit.prevent @update:model-value="isValid => emit('change', isValid)">
    <div class="d-flex flex-column justify-space-between">
      <v-text-field
        v-model="addressData.address1"
        variant="filled"
        :label="`${$t('business.address.address_line_1')} *`"
        :rules="[v => !isEmptyString(v), isString]"
        name="address-address1"
        autocomplete="address-address1"
        data-cy="address1_input"
      />

      <v-text-field
        v-model="addressData.address2"
        variant="filled"
        :label="`${$t('business.address.address_line_2')}`"
        name="address-address2"
        autocomplete="address-address2"
      />

      <v-container class="pa-0">
        <v-row>
          <v-col cols="12" sm="4" :class="{ 'pb-0': $vuetify.display.xs }">
            <v-text-field
              ref="postalCode"
              v-model="addressData.postalCode"
              variant="filled"
              :label="`${$t('billing.settings.address.postcode')} *`"
              :rules="[v => !isEmptyString(v), v => isPostalCode(v, addressData.country ?? undefined)]"
              name="postal-code"
              autocomplete="address-postal-code"
              data-cy="postal_code_input"
            />
          </v-col>
          <v-col cols="12" sm="8" :class="{ 'pt-0': $vuetify.display.xs }">
            <v-text-field
              v-model="addressData.city"
              variant="filled"
              :label="`${$t('billing.settings.address.city')} *`"
              :rules="[v => !isEmptyString(v), isString]"
              autocomplete="address-city"
              data-cy="city_input"
            />
          </v-col>
        </v-row>
      </v-container>

      <v-select
        v-model="addressData.country"
        variant="filled"
        menu-icon="custom:arrow_down"
        :items="languageStore.countryList"
        :label="`* ${$t('billing.settings.address.country')}`"
        :rules="[v => !isEmptyString(v), isString]"
        data-cy="country_input"
        item-title="text"
      />

      <input-vat
        v-model="addressData.vat"
        :title="$t('dialog.upgrade.vat_title')"
        @disable="isDisabled => (isVatDisabled = isDisabled)"
      />
    </div>
  </v-form>
</template>
